import { Client } from "@/Common/Api/Client";
import { UserSettingsConfig } from "@/Common/Models/Settings/UserSettingsConfig";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DraggableImageOverlayElementResponse } from "@/ExportableFile/Api/Dto/DraggableImageOverlayElementResponse";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { DraggableImageOverlayType, OverlayElement, OverlayElementType } from "./OverlayElement";

export class DraggableImageOverlayElement extends OverlayElement {
    public draggableImageOverlayType: DraggableImageOverlayType;
    public left: number = 0;
    public top: number = 0;
    public containerWidth: number = 0;
    public containerHeight: number = 0;
    public width: number = 0;
    public defaultwidth = 0; // Fit image until 200px for intial render
    public height: number = 0;
    public page: number = 1;

    constructor(fields?: Partial<DraggableImageOverlayElement>, generateId: boolean = false) {
        super(OverlayElementType.DraggableImage);
        if (generateId)
            this.id = DraggableImageOverlayElement.generateOverlayId();

        this.update(fields);
    }

    public update(fields?: Partial<DraggableImageOverlayElement>): void {
        Object.assign(this, fields);
    }

    public updateFromJquery(container: JQuery, element: JQuery): void {
        this.update({
            left: element.position().left,
            top: element.position().top,
            containerWidth: container.width(),
            containerHeight: container.height(),
            width: element.width(),
            height: element.height()
        });
    }

    public static Create(type: DraggableImageOverlayType, page: number): DraggableImageOverlayElement {
        const defaultSignatureWidth = 200;
        const signatureWidth = cacheStore.ClinicSettings.EnableQuickDocumentSigning
            ? defaultSignatureWidth
            : (cacheStore.UserSettings?.UserSettingsConfig?.LastSignatureResizePixels ?? defaultSignatureWidth);

        return new DraggableImageOverlayElement({
            id: this.generateOverlayId(),
            draggableImageOverlayType: type,
            page,
            defaultwidth: signatureWidth
        });
    }

    private static generateOverlayId(): string {
        return `FilepreviewElement_${sessionStore.newId()}`;
    }

    public async PersistDefaultSignatureSize(): Promise<void> {
        if (this.draggableImageOverlayType != DraggableImageOverlayType.Signature)
            return;

        if (isNaN(this.width) || this.width <= 0)
            return;

        const userSettings = new UserSettings({
            UserSettingsConfig: new UserSettingsConfig(cacheStore.UserSettings.UserSettingsConfig)
        });
        userSettings.UserSettingsConfig.LastSignatureResizePixels = this.width;

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            }
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        cacheStore.UserSettings.UserSettingsConfig = userSettings.UserSettingsConfig;
    }

    public static fromResponseModel(responseData: DraggableImageOverlayElementResponse): DraggableImageOverlayElement {
        if (!responseData)
            return null;

        return new DraggableImageOverlayElement(
            {
                draggableImageOverlayType: responseData.DraggableImageOverlayType,
                left: responseData.Left,
                top: responseData.Top,
                containerWidth: responseData.ContainerWidth,
                containerHeight: responseData.ContainerHeight,
                width: responseData.Width,
                defaultwidth: responseData.Defaultwidth,
                height: responseData.Height,
                page: responseData.Page
            },
            true
        );
    }

    public static fromResponseModelArray(responseData: DraggableImageOverlayElementResponse[]): DraggableImageOverlayElement[] {
        return responseData?.map(overlay => (this.fromResponseModel(overlay)));
    }
}
